import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Tooltip,
  Alert,
} from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import CloseIcon from "@mui/icons-material/Close";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined";
import RemoveIcon from "@mui/icons-material/Remove";
import CropSquareIcon from "@mui/icons-material/CropSquare";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import TeamMessageList from "../../pages/Team/components/TeamMessageList";
import TeamMessageInput from "../../pages/Team/components/TeamMessageInput";
import TeamTypingIndicator from "../../pages/Team/components/TeamTypingIndicator";
import FileDropHandler from "../MessageHistory/FileDropHandler";
import { useTeamMessages } from "../../hooks/useTeamMessages";
import { useTeamSpaceDetails } from "../../hooks/useTeamSpaces";
import { useRealtimeTeamMessages } from "../../hooks/useRealtimeTeamMessages";
import { useRealtimeTeamTyping } from "../../hooks/useRealtimeTeamTyping";
import { getTeamSpaceLabel } from "./teamSpaceLabel";
import { useNotificationsOptional } from "../../context/NotificationContext";
import TeamForwardDialog from "../../pages/Team/components/TeamForwardDialog";
import {
  markTeamMessagePlayed,
  sendTeamMessage,
  toggleTeamMessageReaction,
} from "../../api/client";
import { buildForwardSendPayload } from "../../pages/Team/utils/teamMessageUtils";
import { emptyTeamMentions } from "../../pages/Team/utils/teamMentions";
import { resolveTeamSpaceLeadId, isMasterTeamDmReadOnly } from "../../pages/Team/utils/teamUiUtils";
import { useAuthContext } from "../../context/AuthContext";
import { openCallPopupWindow } from "../../utils/openCallPopupWindow";
import { openTeamPopupWindow } from "../../utils/openTeamPopupWindow";
import { readTabUserData } from "../../utils/tabAuthStorage";

const WINDOW_WIDTH = 340;
const WINDOW_HEIGHT = 420;
const HEADER_MIN_HEIGHT = 52;

const windowControlSx = {
  width: 36,
  height: 32,
  borderRadius: 0,
  color: "text.secondary",
  "&:hover": {
    bgcolor: "action.hover",
    color: "text.primary",
  },
};

const windowCloseSx = {
  ...windowControlSx,
  "&:hover": {
    bgcolor: "#e81123",
    color: "common.white",
  },
};

const TeamChatFloatingWindow = ({  space,
  position,
  expanded,
  zIndex,
  onPositionChange,
  onExpandedChange,
  onClose,
  onFocus,
  markRead,
  refetchSpaces,
  spaces = [],
}) => {
  const { t } = useTranslation("team");
  const navigate = useNavigate();
  const { userData } = useAuthContext();
  const dmReadOnly = isMasterTeamDmReadOnly(userData, space);
  const notificationsCtx = useNotificationsOptional();
  const composerRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [replyTo, setReplyTo] = useState(null);
  const [forwardMessage, setForwardMessage] = useState(null);
  const [forwardOpen, setForwardOpen] = useState(false);
  const [forwardBusy, setForwardBusy] = useState(false);
  const [editingMessage, setEditingMessage] = useState(null);

  const currentUserId = useMemo(() => {
    try {
      return readTabUserData()?.id ?? null;
    } catch {
      return null;
    }
  }, []);

  const spaceId = space?.id;
  const { data: spaceDetails } = useTeamSpaceDetails(spaceId);
  const members = spaceDetails?.members || [];

  const {
    messages,
    loadMessages,
    loadMore,
    syncLatest,
    syncRecentMessages,
    sendMessage,
    editMessage,
    deleteMessage,
    isSending,
    isLoading,
    loadError,
    hasMore,
    reset,
  } = useTeamMessages(spaceId);

  useEffect(() => {
    reset();
    if (!spaceId) return;
    loadMessages().catch(() => {});
    markRead?.(spaceId)
      ?.catch?.(() => {})
      ?.finally?.(() => refetchSpaces?.());
  }, [spaceId, reset, loadMessages, markRead, refetchSpaces]);

  const registerTeamFloatingSpace = notificationsCtx?.registerTeamFloatingSpace;

  useEffect(() => {
    if (!spaceId) return undefined;
    registerTeamFloatingSpace?.(spaceId, true);
    return () => registerTeamFloatingSpace?.(spaceId, false);
  }, [registerTeamFloatingSpace, spaceId]);

  useRealtimeTeamMessages({
    selectedSpaceId: spaceId,
    syncLatestMessages: syncLatest,
    refetchSpaces,
    markRead,
  });

  const { typingUsers } = useRealtimeTeamTyping({
    spaceId,
    currentUserId,
  });

  const leadId = useMemo(
    () => resolveTeamSpaceLeadId(space, messages),
    [space, messages]
  );

  const handleSend = useCallback(
    async (payload) => {
      if (payload?.editMessageId) {
        const { editMessageId, ...rest } = payload;
        await editMessage({ messageId: editMessageId, ...rest });
        await syncLatest({ refreshRecent: true });
      } else {
        await sendMessage(payload);
        await syncLatest();
      }
    },
    [sendMessage, editMessage, syncLatest]
  );

  const handleDeleteMessage = useCallback(
    async (messageId) => {
      await deleteMessage(messageId);
      await syncLatest({ refreshRecent: true });
    },
    [deleteMessage, syncLatest]
  );

  const handleReply = useCallback((msg) => {
    setEditingMessage(null);
    setReplyTo(msg);
    setForwardOpen(false);
  }, []);

  const handleEdit = useCallback((msg) => {
    setReplyTo(null);
    setForwardOpen(false);
    setEditingMessage(msg);
  }, []);

  const handleDelete = useCallback(
    async (msg) => {
      if (!msg?.id) return;
      if (!window.confirm(t("deleteMessageConfirm"))) return;
      try {
        await handleDeleteMessage(msg.id);
        if (editingMessage?.id === msg.id) setEditingMessage(null);
      } catch (err) {
        console.error("[TeamChatFloatingWindow] delete:", err);
      }
    },
    [handleDeleteMessage, editingMessage?.id, t]
  );

  const handleForwardOpen = useCallback((msg) => {
    setForwardMessage(msg);
    setForwardOpen(true);
  }, []);

  const handleReact = useCallback(
    async (msg, emoji) => {
      if (!msg?.id) return;
      const mine = (msg.reactions || []).find(
        (r) => String(r.userId) === String(currentUserId) && r.emoji === emoji
      );
      try {
        await toggleTeamMessageReaction(msg.id, mine ? "" : emoji);
        await syncRecentMessages();
      } catch (err) {
        console.error("[TeamChatFloatingWindow] reaction:", err);
      }
    },
    [currentUserId, syncRecentMessages]
  );

  const handleAudioPlayed = useCallback(
    async (msg) => {
      if (!msg?.id || msg?.messageType !== "audio") return;
      if (msg.authorType === "user" && String(msg.authorId) === String(currentUserId)) return;
      try {
        await markTeamMessagePlayed(msg.id);
        await syncRecentMessages();
      } catch (err) {
        console.error("[TeamChatFloatingWindow] audio played:", err);
      }
    },
    [currentUserId, syncRecentMessages]
  );

  const handleForwardSubmit = useCallback(
    async ({ targetSpaceId, caption }) => {
      if (!forwardMessage) return;
      setForwardBusy(true);
      try {
        const payload = buildForwardSendPayload(forwardMessage, { caption });
        await sendTeamMessage({
          spaceId: targetSpaceId,
          ...payload,
          mentions: emptyTeamMentions(),
        });
        if (Number(targetSpaceId) === Number(spaceId)) {
          await syncLatest();
        }
        await refetchSpaces?.();
      } finally {
        setForwardBusy(false);
      }
    },
    [forwardMessage, spaceId, syncLatest, refetchSpaces]
  );

  const handleHeaderMouseDown = (e) => {
    if (e.target instanceof HTMLElement && e.target.closest("button")) return;
    e.preventDefault();
    onFocus?.();
    setDragging(true);
    const rect = e.currentTarget.parentElement?.getBoundingClientRect();
    if (!rect) return;
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  };

  useEffect(() => {
    if (!dragging) return undefined;
    const onMove = (e) => {
      e.preventDefault();
      onPositionChange({
        x: Math.max(8, e.clientX - dragOffset.x),
        y: Math.max(64, e.clientY - dragOffset.y),
      });
    };
    const onUp = () => setDragging(false);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [dragging, dragOffset.x, dragOffset.y, onPositionChange]);

  const title = getTeamSpaceLabel(space);

  return (
    <Paper
      elevation={8}
      onMouseDown={onFocus}
      sx={{
        position: "fixed",
        left: position.x,
        top: position.y,
        zIndex,
        width: WINDOW_WIDTH,
        maxWidth: "calc(100vw - 16px)",
        height: expanded ? WINDOW_HEIGHT : "auto",
        minHeight: HEADER_MIN_HEIGHT,
        maxHeight: expanded ? "min(480px, calc(100vh - 80px))" : HEADER_MIN_HEIGHT,
        display: "flex",
        flexDirection: "column",
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        overflow: "hidden",
        boxShadow: (theme) => theme.shadows[12],
        cursor: dragging ? "grabbing" : "default",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          px: 1,
          py: 0.75,
          borderBottom: expanded ? 1 : 0,
          borderColor: "divider",
          bgcolor: "background.paper",
          flexShrink: 0,
          cursor: "grab",
          userSelect: dragging ? "none" : "auto",
        }}
        onMouseDown={handleHeaderMouseDown}
      >
        <DragIndicatorIcon fontSize="small" sx={{ color: "text.secondary" }} />
        <Typography variant="subtitle2" fontWeight={600} noWrap sx={{ flex: 1, minWidth: 0 }}>
          {title}
        </Typography>
        {leadId ? (
          <Tooltip title={t("openRequestTooltip", { id: leadId })}>
            <IconButton
              size="small"
              onClick={() => {
                openCallPopupWindow(leadId, { navigate });
                onClose?.();
              }}
              aria-label={t("openRequest")}
            >
              <ChatOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : null}
        <Tooltip title={t("openPopupWindowTooltip")}>
          <IconButton
            size="small"
            onClick={() => {
              openTeamPopupWindow(spaceId);
            }}
            aria-label={t("openPopupWindow")}
          >
            <ArrowForwardIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Box
          sx={{
            display: "flex",
            alignItems: "stretch",
            ml: 0.25,
            mr: -0.5,
            borderRadius: 1,
            overflow: "hidden",
          }}
        >
          <Tooltip title={t("minimize")}>
            <span>
              <IconButton
                size="small"
                onClick={() => onExpandedChange(false)}
                disabled={!expanded}
                aria-label={t("minimize")}
                sx={windowControlSx}
              >
                <RemoveIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={t("expand")}>
            <span>
              <IconButton
                size="small"
                onClick={() => onExpandedChange(true)}
                disabled={expanded}
                aria-label={t("expand")}
                sx={windowControlSx}
              >
                <CropSquareIcon sx={{ fontSize: 13 }} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={t("close")}>
            <IconButton size="small" onClick={onClose} aria-label={t("close")} sx={windowCloseSx}>
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>      </Box>

      {expanded ? (
        <>
          <FileDropHandler
            onFileUpload={
              dmReadOnly ? undefined : (fileOrFiles) => composerRef.current?.appendFiles(fileOrFiles)
            }
            sx={{
              flex: 1,
              minHeight: 0,
              height: "auto",
              display: "flex",
              flexDirection: "column",
              bgcolor: "background.paper",
            }}
          >
            <TeamMessageList
              listKey={spaceId}
              spaceId={spaceId}
              spaceLeadId={leadId}
              messages={messages}
              currentUserId={currentUserId}
              spaceType={space?.type || "channel"}
              onLoadMore={loadMore}
              hasMore={hasMore}
              isLoading={isLoading}
              loadError={loadError}
              isSending={isSending}
              onReply={dmReadOnly ? undefined : handleReply}
              onEdit={dmReadOnly ? undefined : handleEdit}
              onDelete={dmReadOnly ? undefined : handleDelete}
              onForward={dmReadOnly ? undefined : handleForwardOpen}
              onReact={dmReadOnly ? undefined : handleReact}
              onAudioPlayed={handleAudioPlayed}
              onQuickReply={
                dmReadOnly || !handleSend
                  ? undefined
                  : async (label) => {
                      await handleSend({
                        content: label,
                        mentions: { users: [], agents: [], chatbots: [] },
                      });
                    }
              }
            />
            <TeamTypingIndicator typingUsers={typingUsers} compact />
            {dmReadOnly ? (
              <Alert severity="info" sx={{ mx: 1, mb: 1, flexShrink: 0 }}>
                {t("masterDmReadOnlyHint")}
              </Alert>
            ) : (
            <TeamMessageInput
              ref={composerRef}
              onSend={handleSend}
              isSending={isSending}
              leadId={leadId}
              currentUserId={currentUserId}
              members={members}
              space={space}
              replyTo={replyTo}
              onClearReply={() => setReplyTo(null)}
              editingMessage={editingMessage}
              onCancelEdit={() => setEditingMessage(null)}
              overlayZIndex={(zIndex || 1200) + 200}
            />
            )}
          </FileDropHandler>

          <TeamForwardDialog
            open={forwardOpen}
            onClose={() => {
              setForwardOpen(false);
              setForwardMessage(null);
            }}
            message={forwardMessage}
            spaces={spaces}
            currentSpaceId={spaceId}
            onForward={handleForwardSubmit}
            isSending={forwardBusy}
          />
        </>
      ) : null}
    </Paper>
  );
};

export default TeamChatFloatingWindow;
